/**
 * Files that go with something being sent (a message into a session, the note on an approval
 * answer): uploaded first as drafts, listed with Remove, and handed to the send call as
 * attachmentIds. The hub attaches them to the session or the approval and copies them to the
 * machine. On a phone the picker offers the camera and the photo library.
 */
import { useCallback, useMemo, useState } from 'react';
import { ATTACHMENTS_PER_MESSAGE } from '@orgus/protocol';
import { AttachmentInput, AttachmentList } from '@orgus/ui';
import type { AttachmentView } from '@orgus/ui';
import { removeAttachment, uploaderFor, useAttachmentsState } from './data.ts';

export interface DraftFilesState {
  files: AttachmentView[];
  ids: string[];
  add: (a: AttachmentView) => void;
  /** Take one back (the upload is deleted on the hub). */
  remove: (a: AttachmentView) => void;
  /** Forget the list after a send: the files now belong to what was sent. */
  clear: () => void;
}

export function useDraftFiles(): DraftFilesState {
  const [files, setFiles] = useState<AttachmentView[]>([]);
  const add = useCallback((a: AttachmentView) => setFiles((x) => (x.some((f) => f.id === a.id) ? x : [...x, a])), []);
  const remove = useCallback((a: AttachmentView) => {
    setFiles((x) => x.filter((f) => f.id !== a.id));
    void removeAttachment(a.id).catch(() => undefined);
  }, []);
  const clear = useCallback(() => setFiles([]), []);
  const ids = useMemo(() => files.map((f) => f.id), [files]);
  return { files, ids, add, remove, clear };
}

export interface DraftFilesProps {
  drafts: DraftFilesState;
  disabled?: boolean;
  /** Take screenshots pasted anywhere on the page (one input per view). */
  pasteAnywhere?: boolean;
  /** What happens to the files, under the picker. */
  hint?: string;
  /** Force the camera and photo buttons on or off (default: on touch-first devices). */
  camera?: boolean;
  /** Inside a Composer: a paperclip and one small chip per file, no zone. */
  inline?: boolean;
}

/** The picker and the list. Renders nothing on a hub without the attachments module. */
export function DraftFiles({ drafts, disabled, pasteAnywhere, hint, camera, inline }: DraftFilesProps) {
  const on = !!useAttachmentsState();
  const upload = useMemo(() => uploaderFor(null), []);
  if (!on) return null;
  const full = drafts.files.length >= ATTACHMENTS_PER_MESSAGE;
  if (inline) {
    return (
      <>
        <AttachmentInput
          variant="button"
          upload={upload}
          onUploaded={drafts.add}
          paste={pasteAnywhere && !disabled && !full ? 'document' : 'none'}
          disabled={disabled || full}
          camera={camera}
          hint={full ? `At most ${ATTACHMENTS_PER_MESSAGE} files at a time.` : hint}
        />
        <AttachmentList variant="chips" items={drafts.files} canRemove={() => !disabled} onRemove={drafts.remove} />
      </>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {drafts.files.length > 0 && <AttachmentList items={drafts.files} canRemove={() => !disabled} onRemove={drafts.remove} />}
      <AttachmentInput
        upload={upload}
        onUploaded={drafts.add}
        paste={pasteAnywhere && !disabled && !full ? 'document' : 'none'}
        disabled={disabled || full}
        compact
        camera={camera}
        hint={full ? `At most ${ATTACHMENTS_PER_MESSAGE} files at a time.` : hint}
      />
    </div>
  );
}
